/**
 * FR-66 takeover prompt. Two shapes:
 *   • `kind="prompt"` — user has an active session elsewhere; offer
 *     Yes / No to take over. Yes triggers the force-claim path and kicks
 *     the other device on its next API call. No discards the in-progress
 *     sign-in.
 *   • `kind="blocked"` — user is in an active call on the other device;
 *     they cannot take over from here. Single OK button returns to login.
 */

interface BaseProps {
  email: string;
  onClose: () => void;
}

interface PromptProps extends BaseProps {
  kind: 'prompt';
  onConfirm: () => void;
  busy?: boolean;
}

interface BlockedProps extends BaseProps {
  kind: 'blocked';
}

export type SessionTakeoverProps = PromptProps | BlockedProps;

export function SessionTakeoverModal(props: SessionTakeoverProps) {
  return (
    <div
      className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 space-y-4">
        <h3 className="text-base font-semibold text-slate-900">
          {props.kind === 'blocked' ? 'Active call on another device' : 'Already signed in elsewhere'}
        </h3>

        {props.kind === 'prompt' ? (
          <>
            <p className="text-sm text-slate-700">
              You have an active session on another computer or browser. Do you want to make
              <span className="font-medium"> this </span>
              the current session?
            </p>
            <p className="text-xs text-slate-500">
              If you continue, the other device will be signed out within a few seconds.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={props.onClose}
                disabled={props.busy}
                className="text-sm text-slate-600 hover:text-slate-900 disabled:opacity-50"
              >
                No, cancel
              </button>
              <button
                type="button"
                onClick={props.onConfirm}
                disabled={props.busy}
                className="rounded-lg bg-brand text-white px-3 py-1.5 text-sm font-medium hover:bg-brand-dark disabled:opacity-50"
              >
                {props.busy ? 'Switching…' : 'Yes, sign in here'}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-700">
              You have an active session on another computer <span className="font-medium">and an active call</span>. We can't create a new session while a call is in progress.
            </p>
            <p className="text-xs text-slate-500">
              End the call on the other device, then sign in here.
            </p>
            <div className="flex items-center justify-end pt-2">
              <button
                type="button"
                onClick={props.onClose}
                className="rounded-lg bg-brand text-white px-3 py-1.5 text-sm font-medium hover:bg-brand-dark"
              >
                OK
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
